import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, setAuthToken } from "../lib/api";

const AuthContext = createContext(null);
const TOKEN_KEY = "clyntr_token";
const REFRESH_TOKEN_KEY = "clyntr_refresh_token";
const ORG_LIST_REFRESH_COOLDOWN_MS = 5 * 60 * 1000;

function isSameOrgList(previousRows, nextRows) {
  if (previousRows === nextRows) return true;
  if (!Array.isArray(previousRows) || !Array.isArray(nextRows)) return false;
  if (previousRows.length !== nextRows.length) return false;

  for (let i = 0; i < previousRows.length; i += 1) {
    const previous = previousRows[i] || {};
    const next = nextRows[i] || {};
    if (
      String(previous.id || "") !== String(next.id || "")
      || String(previous.name || "") !== String(next.name || "")
      || String(previous.orgType || "") !== String(next.orgType || "")
      || String(previous.current || "") !== String(next.current || "")
      || String(previous.orgRole || "") !== String(next.orgRole || "")
    ) {
      return false;
    }
  }

  return true;
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem(REFRESH_TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [billing, setBilling] = useState(null);
  const organizationsRef = useRef([]);
  const activeOrgIdRef = useRef("");
  const orgListMetaRef = useRef({ fetchedAt: 0, orgId: "" });
  const orgListRequestRef = useRef(null);

  useEffect(() => {
    organizationsRef.current = organizations;
  }, [organizations]);

  useEffect(() => {
    activeOrgIdRef.current = organization?.id || "";
  }, [organization?.id]);

  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error?.config || {};
        const errCode = error?.response?.data?.error;
        const status = error?.response?.status;

        const isRefreshCall = String(original?.url || "").includes("/api/auth/refresh");
        if (status === 401 && errCode === "TOKEN_EXPIRED" && refreshToken && !original._retry && !isRefreshCall) {
          original._retry = true;
          try {
            const refreshed = await api.post("/api/auth/refresh", { refreshToken });
            const nextToken = refreshed?.data?.token || "";
            const nextRefreshToken = refreshed?.data?.refreshToken || refreshToken;
            if (!nextToken) throw new Error("REFRESH_FAILED");

            localStorage.setItem(TOKEN_KEY, nextToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, nextRefreshToken);
            setAuthToken(nextToken);
            setToken(nextToken);
            setRefreshToken(nextRefreshToken);

            original.headers = {
              ...(original.headers || {}),
              Authorization: `Bearer ${nextToken}`
            };
            return api(original);
          } catch {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            setAuthToken("");
            setToken("");
            setRefreshToken("");
            setUser(null);
            setOrganization(null);
            setOrganizations([]);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, [refreshToken]);

  const refreshOrgList = useCallback(async (options = {}) => {
    const force = typeof options === "boolean" ? options : Boolean(options?.force);
    const now = Date.now();
    const activeOrgId = activeOrgIdRef.current;
    const { fetchedAt, orgId } = orgListMetaRef.current;

    if (!force && orgListRequestRef.current) {
      return orgListRequestRef.current;
    }

    if (
      !force
      && String(orgId || "") === String(activeOrgId || "")
      && now - Number(fetchedAt || 0) < ORG_LIST_REFRESH_COOLDOWN_MS
      && Array.isArray(organizationsRef.current)
      && organizationsRef.current.length > 0
    ) {
      return { organizations: organizationsRef.current };
    }

    const request = api
      .get("/api/org/list")
      .then((res) => {
        const nextRows = Array.isArray(res.data?.organizations) ? res.data.organizations : [];
        organizationsRef.current = nextRows;
        orgListMetaRef.current = { fetchedAt: Date.now(), orgId: activeOrgId || "" };
        setOrganizations((previousRows) => (isSameOrgList(previousRows, nextRows) ? previousRows : nextRows));
        return { ...res.data, organizations: nextRows };
      })
      .catch((error) => {
        if (error?.response?.status === 304) {
          orgListMetaRef.current = { fetchedAt: Date.now(), orgId: activeOrgId || "" };
          return { organizations: organizationsRef.current };
        }
        throw error;
      })
      .finally(() => {
        if (orgListRequestRef.current === request) orgListRequestRef.current = null;
      });

    orgListRequestRef.current = request;
    return request;
  }, []);

  useEffect(() => {
    setAuthToken(token);
    if (!token) {
      setUser(null);
      setOrganization(null);
      activeOrgIdRef.current = "";
      setOrganizations([]);
      organizationsRef.current = [];
      orgListMetaRef.current = { fetchedAt: 0, orgId: "" };
      setIsBootstrapping(false);
      return;
    }

    setIsBootstrapping(true);
    api
      .get("/api/org/me")
      .then(async (res) => {
        const nextUser = res.data?.user || null;
        const nextOrganization = res.data?.organization || null;
        if (!nextOrganization?.id) throw new Error("ORG_NOT_FOUND");
        setUser(nextUser);
        setOrganization(nextOrganization);
        activeOrgIdRef.current = nextOrganization.id;
        await refreshOrgList({ force: true }).catch(() => {});
        try {
          const b = await api.get("/api/billing/usage");
          setBilling(b.data);
        } catch {
          setBilling(null);
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setAuthToken("");
        setToken("");
        setRefreshToken("");
        setUser(null);
        setOrganization(null);
        activeOrgIdRef.current = "";
        setOrganizations([]);
        organizationsRef.current = [];
        orgListMetaRef.current = { fetchedAt: 0, orgId: "" };
        setBilling(null);
      })
      .finally(() => {
        setIsBootstrapping(false);
      });
  }, [token, refreshOrgList]);

  const switchOrganization = useCallback(async (orgId) => {
    if (!orgId) return;
    await api.post(`/api/org/switch/${orgId}`);
    // Refresh the active org/user snapshot.
    const [me, billingRes] = await Promise.all([
      api.get("/api/org/me"),
      api.get("/api/billing/usage").catch(() => null)
    ]);
    setUser(me.data.user);
    setOrganization(me.data.organization);
    activeOrgIdRef.current = me.data?.organization?.id || "";
    setBilling(billingRes?.data || null);
    await refreshOrgList({ force: true }).catch(() => {});
  }, [refreshOrgList]);

  const logout = useCallback(() => {
    const currentToken = token;
    const currentRefreshToken = refreshToken;
    if (currentToken || currentRefreshToken) {
      api
        .post(
          "/api/auth/logout",
          { refreshToken: currentRefreshToken || undefined },
          {
            headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {}
          }
        )
        .catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setAuthToken("");
    setToken("");
    setRefreshToken("");
    setUser(null);
    setOrganization(null);
    activeOrgIdRef.current = "";
    setOrganizations([]);
    organizationsRef.current = [];
    orgListMetaRef.current = { fetchedAt: 0, orgId: "" };
    setBilling(null);
  }, [token, refreshToken]);

  const refreshBilling = useCallback(async () => {
    try {
      const b = await api.get("/api/billing/usage");
      setBilling(b.data);
      return b.data;
    } catch {
      setBilling(null);
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({
      token,
      refreshToken,
      user,
      organization,
      organizations,
      billing,
      isAuthenticated: Boolean(token),
      isBootstrapping,
      setSession(nextToken, payload, session = {}) {
        setIsBootstrapping(true);
        localStorage.setItem(TOKEN_KEY, nextToken);
        if (session.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
          setRefreshToken(session.refreshToken);
        } else {
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          setRefreshToken("");
        }
        setAuthToken(nextToken);
        setToken(nextToken);
        setUser(payload.user);
        setOrganization(payload.organization);
        activeOrgIdRef.current = payload?.organization?.id || "";
      },
      updateOrganization(updates) {
        setOrganization((prev) => ({ ...prev, ...updates }));
      },
      refreshBilling,
      refreshOrgList,
      switchOrganization,
      logout
    }),
    [token, refreshToken, user, organization, organizations, billing, isBootstrapping, refreshBilling, refreshOrgList, switchOrganization, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
